/** @jsxImportSource theme-ui */
import Lightbox from "react-image-lightbox";
import "react-image-lightbox/style.css";
import { FragmentType, graphql, useFragment } from "~/gql";

export interface LightboxImage {
  title: string | null;
  description: string | null;
  fullPreview: { src?: string } | null;
  full: { src?: string; sizes?: string } | null;
}

const fragment = graphql(`
  fragment GalleryLightbox on UploadedFile {
    title
    description
    fullPreview: fluid(maxWidth: 360) {
      src
    }
    full: fluid(maxWidth: 1920, background: "#fff") {
      src
      sizes
    }
  }
`);

export default function GalleryLightbox(props: {
  current: FragmentType<typeof fragment>;
  next: FragmentType<typeof fragment>;
  previous: FragmentType<typeof fragment>;
  onClose: () => void;
  onNext: () => void;
  onPrevious: () => void;
  className?: string;
}) {
  return (
    <InnerLightbox
      sx={{
        ".ril-toolbar-left .ril-toolbar__item__child": {
          variant: "title.small",
        },
        ".ril-caption": { variant: "text.default" },
      }}
      {...props}
    />
  );
}

/**
 * To pass the className into wrapperClassName we need to wrap the component once.
 */
function InnerLightbox({
  current: currentData,
  next: nextData,
  previous: previousData,
  onClose,
  onNext,
  onPrevious,
  className,
  ...rest
}: {
  current: FragmentType<typeof fragment>;
  next: FragmentType<typeof fragment>;
  previous: FragmentType<typeof fragment>;
  onClose: () => void;
  onNext: () => void;
  onPrevious: () => void;
  className?: string;
}) {
  const current = useFragment(fragment, currentData);
  const next = useFragment(fragment, nextData);
  const previous = useFragment(fragment, previousData);
  return (
    <Lightbox
      wrapperClassName={className}
      mainSrc={current?.full?.src || ""}
      mainSrcThumbnail={current?.fullPreview?.src || ""}
      imageTitle={current?.title}
      imageCaption={current?.description}
      nextSrc={next?.full?.src || ""}
      nextSrcThumbnail={next?.fullPreview?.src}
      prevSrc={previous?.full?.src || ""}
      onCloseRequest={onClose}
      onMovePrevRequest={onNext}
      onMoveNextRequest={onPrevious}
      {...rest}
    />
  );
}
